/**
 * AP-46 11.2 — **Detailrahmen eines Baustellendokuments**: Kopf, Zustand, Kette (Nachtrag, Storno,
 * Ersatz), Fachteil der Art, Fotos, Unterschriften mit aufklappbarem Beweisbündel, Zuordnung, und die
 * Handlungen — je nach dem, was der angemeldete Benutzer darf (`can` vom Server).
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronDown, ChevronRight, FileDown, ImagePlus, Mail, PenLine, Smartphone } from 'lucide-react'
import type { SiteDocDetail, SiteDocFileView } from '@symphenia/shared-types'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { bestaetige, frage } from '@/lib/dialoge'
import { downloadFile, extractFilename } from '@/lib/download'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate, fmtDateTime } from '@/lib/format-date'
import { formCard, pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import {
  addSiteDocPhoto,
  captionSiteDocPhoto,
  discardSiteDoc,
  fetchSiteDoc,
  fetchSiteDocFile,
  fetchSiteDocPdf,
  finalizeSiteDoc,
  markSiteDocFileLost,
  removeSiteDocPhoto,
  siteDocCopyRequest,
  supplementSiteDoc,
  takeBackSiteDoc,
} from '../api/site-docs.api'
import { erpArt } from '../arten/registry'
import '../arten'
import { AnGeraetDialog, GegenzeichnenDialog, StornoDialog, ZuordnenDialog } from '../components/DetailDialoge'
import { BestaetigungStand } from '../components/BestaetigungStand'
import { ErpBestaetigung, FreigabeDialog } from '../components/FreigabeDialog'
import { FachteilListe, KanonAnsicht } from '../components/KanonAnsicht'
import { KopfFelder } from '../components/KopfFelder'
import { StatusPill } from '../components/StatusPill'

type Dialog = 'FREIGABE' | 'STORNO' | 'GERAET' | 'ZUORDNEN' | 'GEGEN' | 'KOPIE' | null

function Foto({ dokId, datei, bearbeiten, onText, onWeg }: { dokId: string; datei: SiteDocFileView; bearbeiten?: boolean; onText?: (text: string | null) => void; onWeg?: () => void }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!datei.present) return
    let u: string | null = null
    fetchSiteDocFile(dokId, datei.id)
      .then((b) => {
        u = URL.createObjectURL(b)
        setUrl(u)
      })
      .catch(() => setUrl(null))
    return () => {
      if (u) URL.revokeObjectURL(u)
    }
  }, [dokId, datei.id, datei.present])
  return (
    <figure className="space-y-1">
      {url ? <img src={url} alt={datei.caption ?? ''} className="aspect-square w-full rounded object-cover" /> : <div className="flex aspect-square w-full items-center justify-center rounded bg-muted text-xs text-muted-foreground">{datei.lostAt ? '—' : '…'}</div>}
      {bearbeiten ? (
        <>
          <input className="h-8 w-full rounded border border-input bg-background px-2 text-xs" defaultValue={datei.caption ?? ''} placeholder="…" onBlur={(e) => e.target.value !== (datei.caption ?? '') && onText?.(e.target.value.trim() || null)} />
          <button type="button" className="text-xs text-red-700 hover:underline" onClick={onWeg}>
            ✕
          </button>
        </>
      ) : (
        datei.caption && <figcaption className="text-xs">{datei.caption}</figcaption>
      )}
    </figure>
  )
}

export default function SiteDocDetailPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [dok, setDok] = useState<SiteDocDetail | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [offen, setOffen] = useState<Record<string, boolean>>({})

  const lade = useCallback(async () => {
    try {
      setDok(await fetchSiteDoc(id))
      setFehler(null)
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }, [id, t])

  useEffect(() => {
    void lade()
  }, [lade])

  const handle = async (f: () => Promise<unknown>) => {
    setFehler(null)
    try {
      await f()
      await lade()
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  if (!dok) {
    return <div className={pageWide}>{fehler ? <p className="text-sm text-destructive">{fehler}</p> : <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}</div>
  }

  const art = erpArt(dok.kind)
  const entwurf = dok.status === 'DRAFT' && !dok.heldByUserId
  const eingefroren = !!dok.contentHash
  const fotos = dok.files.filter((f) => f.role === 'PHOTO')
  const fehlend = dok.files.filter((f) => !f.present && !f.lostAt)
  const kanon = dok.canonicalJson ? (JSON.parse(dok.canonicalJson) as Record<string, unknown>) : null
  const knopf = 'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted disabled:opacity-50'

  async function pdf() {
    const r = await fetchSiteDocPdf(dok!.id)
    downloadFile(r.daten, extractFilename(r.disposition, `${dok!.number ?? 'dokument'}.pdf`), 'application/pdf')
  }

  async function verwerfen() {
    const grund = dok!.number ? await frage(t('siteDocs.detail.discardReason'), '', { pflicht: true, ok: t('siteDocs.detail.discard') }) : (await bestaetige(t('siteDocs.detail.discardConfirm'), { ok: t('siteDocs.detail.discard'), gefaehrlich: true })) ? '' : null
    if (grund === null) return
    await handle(() => discardSiteDoc(dok!.kind, dok!.id, grund || undefined))
    navigate('/site-docs', { replace: true })
  }

  async function intern() {
    if (!(await bestaetige(t('siteDocs.detail.finalizeInternalConfirm'), { ok: t('siteDocs.detail.finalizeInternal') }))) return
    await handle(() => finalizeSiteDoc(dok!.kind, dok!.id, 'INTERNAL', []))
  }

  return (
    <div className={pageWide}>
      <div>
        <Link to="/site-docs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('siteDocs.title')}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold text-foreground">{dok.title}</h1>
          <StatusPill status={dok.status} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(`siteDocs.kinds.${dok.kind}`)}
          {dok.number ? ` ${dok.number}` : ''} · {fmtDate(dok.documentDate)} · {dok.projectLabel || '—'} · {t('siteDocs.detail.by', { name: dok.createdByName })}
        </p>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {dok.heldByUserId && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
          <span className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" /> {t('siteDocs.detail.held', { seit: dok.heldSince ? fmtDateTime(dok.heldSince) : '' })}
          </span>
          {dok.can.handToDevice && (
            <button type="button" className={knopf} onClick={() => void handle(() => takeBackSiteDoc(dok.id))}>
              {t('siteDocs.detail.takeBack')}
            </button>
          )}
        </div>
      )}
      {/* Die Ausnahme selbst steht im Server-Protokoll — hier nur, dass etwas aussteht. */}
      {dok.followUpError && <div className="rounded-md bg-amber-100 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('siteDocs.detail.followUpError')}</div>}

      <div className="flex flex-wrap gap-2">
        {entwurf && dok.can.present && dok.allowedReleases.includes('SIGNATURE') && (
          <button type="button" className={`${knopf} border-primary bg-primary text-primary-foreground hover:bg-primary/90`} disabled={dok.missing.length > 0} onClick={() => setDialog('FREIGABE')}>
            <PenLine className="h-4 w-4" /> {t('siteDocs.detail.present')}
          </button>
        )}
        {entwurf && dok.can.finalize && dok.allowedReleases.includes('INTERNAL') && (
          <button type="button" className={knopf} disabled={dok.missing.length > 0} onClick={() => void intern()}>
            {t('siteDocs.detail.finalizeInternal')}
          </button>
        )}
        {entwurf && dok.can.handToDevice && (
          <button type="button" className={knopf} onClick={() => setDialog('GERAET')}>
            <Smartphone className="h-4 w-4" /> {t('siteDocs.detail.handToDevice')}
          </button>
        )}
        {entwurf && dok.can.discard && (
          <button type="button" className={`${knopf} text-red-700`} onClick={() => void verwerfen()}>
            {t('siteDocs.detail.discard')}
          </button>
        )}
        {eingefroren && (
          <button type="button" className={knopf} onClick={() => void handle(pdf)}>
            <FileDown className="h-4 w-4" /> {t('siteDocs.detail.pdf')}
          </button>
        )}
        {eingefroren && dok.status !== 'AWAITING' && dok.can.send && (
          <button type="button" className={knopf} onClick={() => setDialog('KOPIE')}>
            <Mail className="h-4 w-4" /> {t('siteDocs.detail.sendCopy')}
          </button>
        )}
        {(dok.status === 'FINAL' || dok.status === 'AWAITING') && dok.can.countersign && (
          <button type="button" className={knopf} title={dok.status === 'AWAITING' ? t('siteDocs.confirmation.onSiteHint') : undefined} onClick={() => setDialog('GEGEN')}>
            {t('siteDocs.detail.countersign')}
          </button>
        )}
        {dok.can.supplement && (
          <button type="button" className={knopf} onClick={() => void handle(async () => navigate(`/site-docs/${(await supplementSiteDoc(dok.kind, dok.id)).id}`))}>
            {/* AP-49: Bei der Besprechung heißt der Nachtrag „Berichtigung" (7.2). */}
            {t(`siteDocs.detail.supplementKind.${dok.kind}`, t('siteDocs.detail.supplement'))}
          </button>
        )}
        {dok.can.void && (
          <button type="button" className={`${knopf} text-red-700`} disabled={dok.voidBlockers.length > 0} title={dok.voidBlockers.map((b) => t(b, b)).join(' · ')} onClick={() => setDialog('STORNO')}>
            {t('siteDocs.detail.void')}
          </button>
        )}
        {dok.can.assign && (dok.projectSource === 'TEXT' || dok.projectOutsideSelection) && (
          <button type="button" className={knopf} onClick={() => setDialog('ZUORDNEN')}>
            {t('siteDocs.detail.assignProject', { project: terms.project })}
          </button>
        )}
      </div>
      {entwurf && dok.missing.length > 0 && <p className="text-sm text-amber-800 dark:text-amber-200">{t('siteDocs.detail.missing', { liste: [...new Set(dok.missing.map((m) => t(`siteDocs.missing.${m.split(':').pop()}`, t('siteDocs.missing.entry'))))].join(', ') })}</p>}
      {dok.voidBlockers.length > 0 && dok.can.void && <p className="text-sm text-muted-foreground">{t('siteDocs.detail.voidBlocked', { liste: dok.voidBlockers.map((b) => t(b, b)).join(' · ') })}</p>}

      {dok.chain.length > 0 && (
        <div className={`${formCard} text-sm`}>
          <h2 className="mb-1 font-medium">{t('siteDocs.detail.chain')}</h2>
          <ul className="space-y-1">
            {dok.chain.map((k) => (
              <li key={k.id}>
                {t(`siteDocs.chain.${k.relation}`)}{' '}
                <Link to={`/site-docs/${k.id}`} className="text-primary hover:underline">
                  {k.number ?? t('siteDocs.status.DRAFT')}
                </Link>{' '}
                <StatusPill status={k.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {entwurf && dok.can.edit && (
            <div className={formCard}>
              <KopfFelder dok={dok} onGeaendert={setDok} />
            </div>
          )}
          <div className={formCard}>
            {kanon ? (
              <KanonAnsicht objekt={kanon} />
            ) : art?.Detail ? (
              <art.Detail dok={dok} onGeaendert={(neu) => (neu ? setDok(neu) : void lade())} />
            ) : (
              <FachteilListe fachteil={dok.content ?? {}} />
            )}
          </div>
          {kanon && art?.Detail && (
            <div className={formCard}>
              <h2 className="mb-2 text-sm font-medium">{t(art.detailTitel ?? 'siteDocs.detail.assignments')}</h2>
              <art.Detail dok={dok} onGeaendert={(neu) => (neu ? setDok(neu) : void lade())} />
            </div>
          )}
          {(fotos.length > 0 || (entwurf && dok.can.edit)) && (
            <div className={formCard}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-sm font-medium">{t('siteDocs.detail.photos')}</h2>
                {entwurf && dok.can.edit && (
                  <label className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border px-2 text-xs hover:bg-muted">
                    <ImagePlus className="h-3.5 w-3.5" /> {t('siteDocs.detail.addPhoto')}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const dateien = Array.from(e.target.files ?? [])
                        e.target.value = ''
                        for (const d of dateien) await handle(() => addSiteDocPhoto(dok.kind, dok.id, d))
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {fotos.map((f) => (
                  <Foto
                    key={f.id}
                    dokId={dok.id}
                    datei={f}
                    bearbeiten={entwurf && dok.can.edit}
                    onText={(text) => void handle(() => captionSiteDocPhoto(dok.kind, dok.id, f.id, text))}
                    onWeg={() => void handle(() => removeSiteDocPhoto(dok.kind, dok.id, f.id))}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {eingefroren && (
            <div className={formCard}>
              <ErpBestaetigung dok={dok} />
            </div>
          )}
          {dok.confirmation && (
            <div className={formCard}>
              <BestaetigungStand dok={dok} darfSenden={dok.can.send} onGeaendert={setDok} />
            </div>
          )}
          <div className={`${formCard} text-sm`}>
            <h2 className="mb-2 font-medium">{t('siteDocs.detail.signatures')}</h2>
            {dok.signatures.length === 0 && <p className="text-muted-foreground">{dok.status === 'DECLINED' ? t('siteDocs.detail.declined', { grund: dok.declineReason ?? '' }) : t('siteDocs.detail.noSignatures')}</p>}
            <ul className="space-y-2">
              {dok.signatures.map((s) => (
                <li key={s.id} className="rounded-md border px-3 py-2">
                  <button type="button" className="flex w-full items-center gap-1 text-left" onClick={() => setOffen((o) => ({ ...o, [s.id]: !o[s.id] }))}>
                    {offen[s.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="font-medium">{s.name}</span>
                    <span className="text-muted-foreground"> · {t(`siteDocs.roles.${s.role}`)}</span>
                  </button>
                  {offen[s.id] && (
                    <dl className="mt-2 grid grid-cols-[8rem_1fr] gap-x-2 gap-y-0.5 text-xs">
                      <dt className="text-muted-foreground">{t('siteDocs.evidence.channel')}</dt>
                      <dd>{t(`siteDocs.channels.${s.channel}`)}</dd>
                      {s.function && (
                        <>
                          <dt className="text-muted-foreground">{t('siteDocs.release.function')}</dt>
                          <dd>{s.function}</dd>
                        </>
                      )}
                      {s.email && (
                        <>
                          <dt className="text-muted-foreground">{t('siteDocs.evidence.email')}</dt>
                          <dd>{s.email}</dd>
                        </>
                      )}
                      <dt className="text-muted-foreground">{t('siteDocs.evidence.signedAt')}</dt>
                      <dd>{fmtDateTime(s.signedAtDevice)}</dd>
                      <dt className="text-muted-foreground">{t('siteDocs.evidence.hash')}</dt>
                      <dd className="font-mono">{s.contentHash.slice(0, 16)}…</dd>
                      {s.deviceId && (
                        <>
                          <dt className="text-muted-foreground">{t('siteDocs.evidence.device')}</dt>
                          <dd className="font-mono">{s.deviceId.slice(0, 8)}</dd>
                        </>
                      )}
                      <dt className="text-muted-foreground">{t('siteDocs.evidence.offline')}</dt>
                      <dd>{s.capturedOffline ? t('siteDocs.evidence.yes') : t('siteDocs.evidence.no')}</dd>
                      {s.userAgent && (
                        <>
                          <dt className="text-muted-foreground">{t('siteDocs.evidence.userAgent')}</dt>
                          <dd className="break-all">{s.userAgent}</dd>
                        </>
                      )}
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {fehlend.length > 0 && (
            <div className={`${formCard} text-sm`}>
              <h2 className="mb-1 font-medium text-destructive">{t('siteDocs.detail.filesMissing')}</h2>
              <ul className="space-y-1">
                {fehlend.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2">
                    <span>
                      {t(`siteDocs.fileRoles.${f.role}`)} {f.caption ?? ''}
                    </span>
                    {dok.can.assign && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={async () => {
                          const grund = await frage(t('siteDocs.detail.markLostConfirm'), '', { pflicht: true, ok: t('siteDocs.detail.markLost') })
                          if (grund) await handle(() => markSiteDocFileLost(dok.id, f.id, grund))
                        }}
                      >
                        {t('siteDocs.detail.markLost')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className={`${formCard} text-sm`}>
            <h2 className="mb-1 font-medium">{t('siteDocs.detail.facts')}</h2>
            <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-0.5 text-xs">
              <dt className="text-muted-foreground">{terms.project}</dt>
              <dd>
                {dok.projectLabel || '—'}
                {dok.projectSource === 'TEXT' && ` (${t('siteDocs.detail.freeEntry')})`}
              </dd>
              {dok.snapshot.customer && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.preview.customer')}</dt>
                  <dd>{dok.snapshot.customer}</dd>
                </>
              )}
              {dok.receivedAt && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.columns.receivedAt')}</dt>
                  <dd>{fmtDateTime(dok.receivedAt)}</dd>
                </>
              )}
              {dok.clockSkewSeconds != null && Math.abs(dok.clockSkewSeconds) > 300 && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.detail.clockSkew')}</dt>
                  <dd>{Math.round(dok.clockSkewSeconds / 60)} min</dd>
                </>
              )}
              {dok.copySentAt && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.columns.copy')}</dt>
                  <dd>{fmtDateTime(dok.copySentAt)}</dd>
                </>
              )}
              {dok.fetchedAt && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.columns.fetchedAt')}</dt>
                  <dd>{fmtDateTime(dok.fetchedAt)}</dd>
                </>
              )}
              {dok.voidReason && (
                <>
                  <dt className="text-muted-foreground">{t('siteDocs.detail.voidReason')}</dt>
                  <dd>{dok.voidReason}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
      </div>

      {dialog === 'FREIGABE' && <FreigabeDialog dok={dok} offen onClose={() => { setDialog(null); void lade() }} onFertig={(neu) => { setDialog(null); setDok(neu) }} />}
      {dialog === 'STORNO' && <StornoDialog dok={dok} onClose={() => setDialog(null)} onFertig={(ersatz) => { setDialog(null); if (ersatz) navigate(`/site-docs/${ersatz}`); else void lade() }} />}
      {dialog === 'GERAET' && <AnGeraetDialog dok={dok} onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void lade() }} />}
      {dialog === 'ZUORDNEN' && <ZuordnenDialog dok={dok} onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void lade() }} />}
      {dialog === 'GEGEN' && <GegenzeichnenDialog dok={dok} onClose={() => setDialog(null)} onFertig={() => { setDialog(null); void lade() }} />}
      {dialog === 'KOPIE' && (
        <MailExportDialog
          open
          onClose={() => { setDialog(null); void lade() }}
          source="SITE_DOCUMENT"
          title={t('mailExport.titles.SITE_DOCUMENT')}
          defaultSubject={`${t(`siteDocs.kinds.${dok.kind}`)} ${dok.number ?? ''}`.trim()}
          request={() => siteDocCopyRequest(dok.id)}
          formats={['pdf']}
        />
      )}
    </div>
  )
}
